import React from 'react';
import styles from './BadgesTab.module.css';

const BadgesTab = ({ user, missions = [], lessons = [], quizzes = [], tabId = "badges", tabLabel = "Badges" }) => {
  // Calculate achievements based on mission, lesson, and quiz progress
  const completedMissions = missions.filter(mission => mission.status === 'completed').length;
  const completedLessons = lessons.filter(lesson => lesson.status === 'completed').length;
  const completedQuizzes = quizzes.filter(quiz => quiz.assignmentStatus === 'completed' || quiz.assignmentStatus === 'graded').length;
  const totalStars = user?.totalStars || 0;
  
  // Calculate quiz performance statistics
  const quizScores = quizzes
    .filter(quiz => quiz.percentage !== undefined && quiz.percentage !== null)
    .map(quiz => quiz.percentage);
  const averageQuizScore = quizScores.length > 0 
    ? Math.round(quizScores.reduce((sum, score) => sum + score, 0) / quizScores.length)
    : 0;
  
  // Calculate comprehensive progress (missions + lessons + quizzes)
  const totalAssigned = missions.length + lessons.length + quizzes.length;
  const totalCompleted = completedMissions + completedLessons + completedQuizzes;
  const progressPercentage = totalAssigned > 0 ? Math.round((totalCompleted / totalAssigned) * 100) : 0;
  
  // Define available badges/achievements
  const availableBadges = [
    {
      id: 'first-assignment',
      name: 'First Steps',
      description: 'You\'ve completed your very first assignment! Great job taking the first step in your learning journey.',
      icon: '🌱',
      color: '#28a745',
      earned: totalCompleted >= 1,
      requirement: 'Complete 1 assignment'
    },
    {
      id: 'assignment-explorer-5',
      name: 'Assignment Explorer',
      description: 'You\'re becoming a true explorer! You\'ve completed 5 assignments and are well on your way to mastery.',
      icon: '🗺️',
      color: '#17a2b8',
      earned: totalCompleted >= 5,
      requirement: 'Complete 5 assignments'
    },
    {
      id: 'assignment-master-10',
      name: 'Assignment Master',
      description: 'Incredible! You\'ve completed 10 assignments and have truly mastered the art of learning.',
      icon: '🏆',
      color: '#ffc107',
      earned: totalCompleted >= 10,
      requirement: 'Complete 10 assignments'
    },
    {
      id: 'star-collector-10',
      name: 'Star Collector',
      description: 'Amazing! You\'ve earned 10 stars from your teachers. Your hard work is really shining through!',
      icon: '⭐',
      color: '#fd7e14',
      earned: totalStars >= 10,
      requirement: 'Earn 10 stars'
    },
    {
      id: 'star-collector-25',
      name: 'Star Hunter',
      description: 'Outstanding! You\'ve earned 25 stars and are truly a star hunter. Your dedication is inspiring!',
      icon: '🌟',
      color: '#6f42c1',
      earned: totalStars >= 25,
      requirement: 'Earn 25 stars'
    },
    {
      id: 'dedicated-learner',
      name: 'Dedicated Learner',
      description: 'You\'re showing real dedication to your learning! You\'ve completed 3 missions and are building great habits.',
      icon: '📚',
      color: '#20c997',
      earned: completedMissions >= 3,
      requirement: 'Complete 3 missions'
    },
    {
      id: 'quiz-master',
      name: 'Quiz Master',
      description: 'Amazing! You\'ve completed your first quiz and are mastering the art of testing your knowledge.',
      icon: '❓',
      color: '#7f14c7',
      earned: completedQuizzes >= 1,
      requirement: 'Complete 1 quiz'
    },
    {
      id: 'quiz-champion',
      name: 'Quiz Champion',
      description: 'Outstanding! You\'ve completed 3 quizzes and are becoming a true quiz champion!',
      icon: '🏆',
      color: '#a93dea',
      earned: completedQuizzes >= 3,
      requirement: 'Complete 3 quizzes'
    },
    {
      id: 'high-achiever',
      name: 'High Achiever',
      description: 'Incredible! You\'re achieving high scores on your quizzes with an average of 90% or higher!',
      icon: '⭐',
      color: '#ffd700',
      earned: averageQuizScore >= 90,
      requirement: 'Average quiz score of 90%+'
    },
    {
      id: 'excellent-student',
      name: 'Excellent Student',
      description: 'Fantastic! You\'re maintaining excellent performance with an average quiz score of 80% or higher!',
      icon: '🌟',
      color: '#ff6b35',
      earned: averageQuizScore >= 80,
      requirement: 'Average quiz score of 80%+'
    },
    {
      id: 'balanced-learner',
      name: 'Balanced Learner',
      description: 'Perfect! You\'re excelling in all areas - missions, lessons, and quizzes. You\'re a truly balanced learner!',
      icon: '⚖️',
      color: '#4ecdc4',
      earned: completedMissions >= 2 && completedLessons >= 2 && completedQuizzes >= 2,
      requirement: 'Complete 2+ missions, lessons, and quizzes'
    },
    {
      id: 'knowledge-seeker',
      name: 'Knowledge Seeker',
      description: 'Wonderful! You\'ve completed 5 quizzes and are truly seeking knowledge through testing!',
      icon: '🔍',
      color: '#45b7d1',
      earned: completedQuizzes >= 5,
      requirement: 'Complete 5 quizzes'
    },
    {
      id: 'perfect-score',
      name: 'Perfect Score',
      description: 'Incredible! You\'ve achieved a perfect 100% score on at least one quiz!',
      icon: '💯',
      color: '#ff4757',
      earned: quizScores.some(score => score === 100),
      requirement: 'Achieve 100% on any quiz'
    }
  ];

  const earnedBadges = availableBadges.filter(badge => badge.earned);
  const unearnedBadges = availableBadges.filter(badge => !badge.earned);

  return (
    <div tabId={tabId} tabLabel={tabLabel} className={styles.badgesContainer}>
      {/* Stats Overview */}
      <div>
        <h5 className={styles.sectionTitle}>
          <span className={styles.sectionTitleIcon}>📊</span>
          Your Progress
        </h5>
        <div className={styles.statsGrid}>
        <div className={styles.statsCard}>
          <i className={`fas fa-medal ${styles.statsIcon}`} style={{ color: '#ffc107' }}></i>
          <div className={styles.statsTitle}>Badges Earned</div>
          <div className={styles.statsValue} style={{ color: '#ffc107' }}>{earnedBadges.length}</div>
        </div>
        <div className={styles.statsCard}>
          <i className={`fas fa-trophy ${styles.statsIcon}`} style={{ color: '#28a745' }}></i>
          <div className={styles.statsTitle}>Assignments Completed</div>
          <div className={styles.statsValue} style={{ color: '#28a745' }}>{totalCompleted}</div>
        </div>
        <div className={styles.statsCard}>
          <i className={`fas fa-question-circle ${styles.statsIcon}`} style={{ color: '#7f14c7' }}></i>
          <div className={styles.statsTitle}>Quizzes Completed</div>
          <div className={styles.statsValue} style={{ color: '#7f14c7' }}>{completedQuizzes}</div>
        </div>
        <div className={styles.statsCard}>
          <i className={`fas fa-star ${styles.statsIcon}`} style={{ color: '#ffc107' }}></i>
          <div className={styles.statsTitle}>Total Stars</div>
          <div className={styles.statsValue} style={{ color: '#ffc107' }}>{totalStars}</div>
        </div>
        <div className={styles.statsCard}>
          <i className={`fas fa-chart-line ${styles.statsIcon}`} style={{ color: '#17a2b8' }}></i>
          <div className={styles.statsTitle}>Progress</div>
          <div className={styles.statsValue} style={{ color: '#17a2b8' }}>{progressPercentage}%</div>
        </div>
        {averageQuizScore > 0 && (
          <div className={styles.statsCard}>
            <i className={`fas fa-percentage ${styles.statsIcon}`} style={{ color: '#ff6b35' }}></i>
            <div className={styles.statsTitle}>Avg Quiz Score</div>
            <div className={styles.statsValue} style={{ color: '#ff6b35' }}>{averageQuizScore}%</div>
          </div>
        )}
        </div>
      </div>

      {/* Earned Badges */}
      {earnedBadges.length > 0 && (
        <div>
          <h5 className={styles.sectionTitle}>
            <span className={styles.sectionTitleIcon}>🏆</span>
            Your Achievements
          </h5>
          <div className={styles.badgesGrid}>
            {earnedBadges.map((badge) => (
              <div key={badge.id} className={`${styles.badgeCard} ${styles.badgeCardEarned}`}>
                <div className={styles.badgeIcon}>
                  {badge.icon}
                </div>
                <h6 className={`${styles.badgeTitle} ${styles.badgeTitleEarned}`}>{badge.name}</h6>
                <p className={styles.badgeDescription}>{badge.description}</p>
                <div className={`${styles.badgeStatus} ${styles.badgeStatusEarned}`}>Earned!</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Badges */}
      {unearnedBadges.length > 0 && (
        <div>
          <h5 className={styles.sectionTitle}>
            <span className={styles.sectionTitleIcon}>🎯</span>
            Available Badges
          </h5>
          <div className={styles.badgesGrid}>
            {unearnedBadges.map((badge) => (
              <div key={badge.id} className={`${styles.badgeCard} ${styles.badgeCardUnearned}`}>
                <div className={`${styles.badgeIcon} ${styles.badgeIconUnearned}`}>
                  {badge.icon}
                </div>
                <h6 className={`${styles.badgeTitle} ${styles.badgeTitleUnearned}`}>{badge.name}</h6>
                <p className={styles.badgeRequirement}>{badge.requirement}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No badges message */}
      {earnedBadges.length === 0 && (
        <div className={styles.noBadgesMessage}>
          <i className={`fas fa-medal ${styles.noBadgesIcon}`}></i>
          <h5 className={styles.noBadgesTitle}>No badges earned yet</h5>
          <p className={styles.noBadgesText}>Complete missions to earn your first badge!</p>
        </div>
      )}
    </div>
  );
};

export default BadgesTab; 